/**
 * Automation UI Store
 *
 * Manages transient UI state for the automation page (active view, selected step, etc.).
 * NOT persisted — resets on navigation away or logout.
 *
 * @module store/automation/automationUIStore
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

const INITIAL_UI_STATE = {
  activeView: 'script',     // 'script' | 'settings' | 'results'
  activeStepId: null,
  activeFileTab: null,       // 'request' | 'response' | null
  isFullscreen: false,
};

export const useAutomationUIStore = create((set) => ({
  ...INITIAL_UI_STATE,

  setActiveView: (view) => set({ activeView: view }),

  setActiveStepId: (stepId) =>
    set(state => ({
      activeStepId: state.activeStepId === stepId ? null : stepId,
    })),

  setActiveFileTab: (tab) =>
    set(state => ({
      activeFileTab: state.activeFileTab === tab ? null : tab,
    })),

  toggleFullscreen: () =>
    set(state => ({ isFullscreen: !state.isFullscreen })),

  resetUI: () => set(INITIAL_UI_STATE),
}));

// ── Granular Selector Hooks ─────────────────────────────────────

export const useActiveView = () =>
  useAutomationUIStore(state => state.activeView);

export const useActiveStepId = () =>
  useAutomationUIStore(state => state.activeStepId);

export const useActiveFileTab = () =>
  useAutomationUIStore(state => state.activeFileTab);

export const useIsFullscreen = () =>
  useAutomationUIStore(state => state.isFullscreen);

export const useAutomationUIActions = () =>
  useAutomationUIStore(
    useShallow(state => ({
      setActiveView: state.setActiveView,
      setActiveStepId: state.setActiveStepId,
      setActiveFileTab: state.setActiveFileTab,
      toggleFullscreen: state.toggleFullscreen,
      resetUI: state.resetUI,
    }))
  );
